import { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { NativeSelect } from "@/components/ui/native-select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Plus, ArrowLeft, MapPin, Calendar as CalendarIcon, Download, Link as LinkIcon, Share2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { extractErrorMessage } from "@/lib/errorMessage";

function ProjectsList() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      toast.error("Erro ao carregar projetos");
    } finally {
      setLoading(false);
    }
  };

  const generateTrackingLink = async (projectId) => {
    try {
      const response = await axios.post(`${API}/projects/${projectId}/tracking-link?expires_hours=72`);
      const url = `${window.location.origin}${response.data.tracking_url}`;
      await navigator.clipboard.writeText(url);
      toast.success("Link de acompanhamento copiado!");
    } catch (error) {
      toast.error("Erro ao gerar link");
    }
  };

  const generateCadernoDeObra = async (projectId) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/caderno-de-obra`);
      toast.success("Caderno de Obra gerado!");
      console.log("Caderno:", response.data);
    } catch (error) {
      toast.error("Erro ao gerar Caderno de Obra");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luvcorp-navy"></div>
      </div>
    );
  }

  const statusColors = {
    active: "bg-emerald-100 text-emerald-700",
    completed: "bg-blue-100 text-blue-700",
    on_hold: "bg-amber-100 text-amber-700"
  };

  const statusLabels = {
    active: "Em Andamento",
    completed: "Concluído",
    on_hold: "Pausado"
  };

  return (
    <div className="space-y-6" data-testid="projects-list">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Projetos</h1>
        <Link to="/projects/new">
          <Button className="bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white" data-testid="new-project-btn">
            <Plus className="h-4 w-4 mr-2" />
            Novo Projeto
          </Button>
        </Link>
      </div>

      {projects.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <p className="text-slate-500">Nenhum projeto cadastrado</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {projects.map((project) => (
            <Card key={project.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg font-heading text-luvcorp-navy">{project.name}</CardTitle>
                    <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                      <MapPin className="h-3 w-3" />
                      {project.location}
                    </p>
                  </div>
                  <Badge className={statusColors[project.status] || "bg-slate-100"}>
                    {statusLabels[project.status] || project.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-600 line-clamp-2">{project.description}</p>
                
                {project.client_name && (
                  <p className="text-sm text-slate-500">
                    <span className="font-medium">Cliente:</span> {project.client_name}
                  </p>
                )}

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Início</p>
                    <p className="font-medium">{new Date(project.start_date).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Término Prev.</p>
                    <p className="font-medium">{new Date(project.estimated_end_date).toLocaleDateString('pt-BR')}</p>
                  </div>
                </div>

                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Valor do Contrato</p>
                  <p className="text-xl font-bold text-luvcorp-navy">
                    R$ {project.contract_value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => generateTrackingLink(project.id)}
                    className="flex-1"
                    data-testid={`tracking-btn-${project.id}`}
                  >
                    <Share2 className="h-4 w-4 mr-1" />
                    Link
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => generateCadernoDeObra(project.id)}
                    className="flex-1 bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white"
                    data-testid={`caderno-btn-${project.id}`}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Caderno
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function NewProject() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    client_id: "",
    location: "",
    contract_value: 0,
    installments: []
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/users?role=client`);
      setClients(response.data);
    } catch (error) {
      console.log("Error fetching clients:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        start_date: startDate ? format(startDate, "yyyy-MM-dd") : "",
        estimated_end_date: endDate ? format(endDate, "yyyy-MM-dd") : ""
      };
      
      await axios.post(`${API}/projects`, data);
      toast.success("Projeto criado com sucesso!");
      navigate("/projects");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao criar projeto"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="new-project">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/projects">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Novo Projeto</h1>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Nome do Projeto *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ex: Reforma do Edifício Central"
                required
                data-testid="input-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Descreva o escopo do projeto..."
                rows={4}
                required
                data-testid="input-description"
              />
            </div>

            <div className="space-y-2">
              <Label>Cliente *</Label>
              <NativeSelect
                value={formData.client_id}
                onValueChange={(value) => setFormData({...formData, client_id: value})}
                placeholder="Selecione o cliente"
                options={clients.map(c => ({ value: c.id, label: c.name }))}
                data-testid="select-client"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Localização *</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                placeholder="Endereço completo"
                required
                data-testid="input-location"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data de Início *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn("w-full justify-start text-left font-normal", !startDate && "text-muted-foreground")}
                      data-testid="date-start"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecione"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Previsão de Término *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn("w-full justify-start text-left font-normal", !endDate && "text-muted-foreground")}
                      data-testid="date-end"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecione"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contract_value">Valor do Contrato *</Label>
              <Input
                id="contract_value"
                type="number"
                min="0"
                step="0.01"
                value={formData.contract_value}
                onChange={(e) => setFormData({...formData, contract_value: parseFloat(e.target.value) || 0})}
                placeholder="0.00"
                required
                data-testid="input-contract-value"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white h-11"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? "Criando..." : "Criar Projeto"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default function ProjectsPage() {
  return (
    <MainLayout>
      <Routes>
        <Route index element={<ProjectsList />} />
        <Route path="new" element={<NewProject />} />
      </Routes>
    </MainLayout>
  );
}
